'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { CookieManager } from '@/lib/cookies'

/**
 * Minimal cookie consent banner component
 * Appears at bottom of page for GDPR compliance
 */
export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    // Only run on client side
    setIsLoaded(true)
    
    // Check if we should show the banner
    if (CookieManager.shouldShowConsentBanner()) {
      setIsVisible(true)
    }
  }, [])

  const handleAcceptAll = () => {
    CookieManager.acceptAll()
    setIsVisible(false)
  }

  const handleAcceptEssential = () => {
    CookieManager.acceptEssentialOnly()
    setIsVisible(false)
  }

  const handleDecline = () => {
    CookieManager.declineAll()
    setIsVisible(false)
  }

  // Don't render on server or if not visible
  if (!isLoaded || !isVisible) {
    return null
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 transform transition-transform duration-300 ease-in-out">
      {/* Background overlay for mobile */}
      <div className="bg-base-300/95 backdrop-blur-sm border-t border-base-content/10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            {/* Message */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                <div className="flex items-center gap-2">
                  <svg 
                    className="w-5 h-5 text-primary flex-shrink-0" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                    />
                  </svg>
                  <p className="text-sm text-base-content font-medium">
                    We use cookies to enhance your experience
                  </p>
                </div>
                <div className="text-xs text-base-content/70 sm:text-sm">
                  By continuing to use our site, you consent to our use of cookies.{' '}
                  <Link 
                    href="/cookie-policy" 
                    className="link link-primary font-medium hover:link-accent"
                  >
                    Learn more
                  </Link>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-3 lg:flex-shrink-0">
              {/* Quick Actions - Mobile */}
              <div className="flex gap-2 sm:hidden">
                <button
                  onClick={handleAcceptAll}
                  className="btn btn-primary btn-sm flex-1"
                  aria-label="Accept all cookies"
                >
                  Accept All
                </button>
                <button
                  onClick={handleAcceptEssential}
                  className="btn btn-outline btn-sm flex-1"
                  aria-label="Accept only essential cookies"
                >
                  Essential Only
                </button>
              </div>

              {/* Desktop Actions */}
              <div className="hidden sm:flex sm:gap-3">
                <button
                  onClick={handleDecline}
                  className="btn btn-ghost btn-sm text-base-content/70 hover:text-base-content"
                  aria-label="Decline all non-essential cookies"
                >
                  Decline
                </button>
                <button
                  onClick={handleAcceptEssential}
                  className="btn btn-outline btn-sm"
                  aria-label="Accept only essential cookies"
                >
                  Essential Only
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="btn btn-primary btn-sm"
                  aria-label="Accept all cookies"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Advanced cookie preferences component (for future use)
 * Can be used in a modal or separate preferences page
 */
interface CookiePreferencesProps {
  onSave: (preferences: { analytics: boolean; marketing: boolean }) => void
  onClose: () => void
  currentPreferences?: { analytics: boolean; marketing: boolean } | null
}

export function CookiePreferences({ onSave, onClose, currentPreferences }: CookiePreferencesProps) {
  const [analytics, setAnalytics] = useState(currentPreferences?.analytics ?? false)
  const [marketing, setMarketing] = useState(currentPreferences?.marketing ?? false)

  const handleSave = () => {
    onSave({ analytics, marketing })
    onClose()
  }

  return (
    <div className="bg-base-100 rounded-lg p-6 max-w-md mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-base-content">
          Cookie Preferences
        </h3>
        <button
          onClick={onClose}
          className="btn btn-ghost btn-sm btn-circle"
          aria-label="Close preferences"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="space-y-4">
        {/* Essential Cookies */}
        <div className="flex items-start justify-between p-3 bg-base-200 rounded-lg">
          <div className="flex-1">
            <h4 className="font-medium text-base-content mb-1">Essential Cookies</h4>
            <p className="text-sm text-base-content/70">
              Required for basic website functionality and security.
            </p>
          </div>
          <div className="form-control">
            <input 
              type="checkbox" 
              checked={true} 
              disabled={true}
              className="toggle toggle-primary" 
              aria-label="Essential cookies (always enabled)"
            />
          </div>
        </div>

        {/* Analytics Cookies */}
        <div className="flex items-start justify-between p-3 border border-base-300 rounded-lg">
          <div className="flex-1">
            <h4 className="font-medium text-base-content mb-1">Analytics Cookies</h4>
            <p className="text-sm text-base-content/70">
              Help us understand how visitors use our website to improve performance.
            </p>
          </div>
          <div className="form-control">
            <input 
              type="checkbox" 
              checked={analytics}
              onChange={(e) => setAnalytics(e.target.checked)}
              className="toggle toggle-primary" 
              aria-label="Enable analytics cookies"
            />
          </div>
        </div>

        {/* Marketing Cookies */}
        <div className="flex items-start justify-between p-3 border border-base-300 rounded-lg">
          <div className="flex-1">
            <h4 className="font-medium text-base-content mb-1">Marketing Cookies</h4>
            <p className="text-sm text-base-content/70">
              Used to deliver personalized advertisements and track campaign effectiveness.
            </p>
          </div>
          <div className="form-control">
            <input 
              type="checkbox" 
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              className="toggle toggle-primary" 
              aria-label="Enable marketing cookies"
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 mt-6">
        <button
          onClick={onClose}
          className="btn btn-outline flex-1"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className="btn btn-primary flex-1"
        >
          Save Preferences
        </button>
      </div>

      {/* Policy Link */}
      <div className="text-center mt-4">
        <Link 
          href="/cookie-policy" 
          className="text-sm text-base-content/70 hover:text-base-content link"
        >
          Read our full Cookie Policy
        </Link>
      </div>
    </div>
  )
}